import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@verlo/shared/components/ui/button';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Signal } from '@verlo/shared/components/ui/signal';
import { modifiersApi } from '@verlo/shared/services/api';
import { useApp } from '@verlo/shared/contexts/AppContext';
import { toast } from 'sonner';
import { useSite } from '../../../contexts/SiteContext';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { PageHeader, Stat, Stats, DataTable, FilterBar, Empty } from '../../../components/office';

/**
 * Reports › Stock & people › Modifiers (modifiers plan, wave 2): what people change and
 * what the extras earn. Per item the take-up and the options taken; the top earners; and
 * the most-typed free text — with one button to make it a library modifier so it stops
 * being typed.
 */
const RANGES = [{ value: '7', label: 'Last 7 days' }, { value: '28', label: 'Last 4 weeks' }, { value: '91', label: 'Last 13 weeks' }];
const iso = (d) => d.toISOString().slice(0, 10);

const ModifiersReportPage = () => {
  const { formatCurrency } = useApp();
  const { site } = useSite();
  const [range, setRange] = useState('28');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);

  const dates = useMemo(() => {
    const to = new Date(); const from = new Date(to.getTime() - (parseInt(range, 10) - 1) * 864e5);
    return { date_from: iso(from), date_to: iso(to) };
  }, [range]);

  const load = () => {
    setLoading(true);
    modifiersApi.report({ ...dates, ...(site ? { site } : {}) })
      .then(({ data: d }) => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(load, [dates, site]); // eslint-disable-line react-hooks/exhaustive-deps

  const makeModifier = async (t) => {
    setAdding(t.text);
    try {
      await modifiersApi.create({ name: t.text.replace(/^\w/, (c) => c.toUpperCase()), price: 0, kind: 'instruction', group: 'Typed', is_active: true, category: 'add_on' });
      toast.success(`“${t.text}” is in the library. The till will find it instead of it being typed`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add it"); }
    finally { setAdding(null); }
  };

  const items = data?.items || [];
  const typed = data?.typed || [];
  const earners = data?.earners || [];
  const t = data?.totals || {};
  const takeUp = t.sold ? Math.round((100 * t.changed) / t.sold) : 0;

  const itemColumns = [
    { key: 'name', label: 'Item', sortable: true, render: (r) => <span className="font-semibold text-ink">{r.name}</span> },
    { key: 'sold', label: 'Sold', align: 'right', mono: true, sortable: true },
    { key: 'take_up', label: 'Changed', align: 'right', mono: true, sortable: true, render: (r) => <span className={r.take_up >= 50 ? 'font-semibold text-ink' : ''}>{r.take_up}%</span> },
    { key: 'options', label: 'What they take', className: 'min-w-[18rem]', render: (r) => (
      <span className="flex flex-wrap gap-1.5">
        {r.options.slice(0, 5).map((o) => <span key={o.name} className="inline-flex items-center gap-1 border border-hairline bg-surface-2 px-2 py-0.5 text-[12px] text-ink-soft"><span className="font-semibold text-ink">{o.share}%</span> {o.name}</span>)}
        {r.options.length === 0 && <span className="text-ink-faint">as it comes</span>}
      </span>
    ) },
    { key: 'extras_revenue', label: 'From extras', align: 'right', mono: true, sortable: true, render: (r) => r.extras_revenue ? formatCurrency(r.extras_revenue) : <span className="text-ink-faint">—</span> },
  ];
  const typedColumns = [
    { key: 'text', label: 'Typed', render: (r) => <span className="text-ink">“{r.text}”</span> },
    { key: 'count', label: 'Times', align: 'right', mono: true, sortable: true },
    { key: 'priced', label: 'Priced', align: 'right', mono: true, render: (r) => r.priced ? <span className="text-signal-warn">{r.priced} · {formatCurrency(r.value)}</span> : <span className="text-ink-faint">notes</span> },
    { key: 'in_library', label: '', render: (r) => r.in_library ? <Signal tone="ok" dot={false}>in the library</Signal> : (
      <Button size="sm" variant="outline" className="h-8" disabled={adding === r.text} onClick={(e) => { e.stopPropagation(); makeModifier(r); }} data-testid={`make-modifier-${r.text.replace(/\W+/g, '-')}`}>Make it a modifier</Button>
    ) },
  ];
  const earnerColumns = [
    { key: 'name', label: 'Modifier', render: (r) => <span className="font-semibold text-ink">{r.name}</span> },
    { key: 'revenue_line', label: 'Line', render: (r) => r.revenue_line ? <Signal tone={r.revenue_line === 'drink' ? 'info' : r.revenue_line === 'food' ? 'ok' : 'warn'} dot={false}>{r.revenue_line}</Signal> : '' },
    { key: 'count', label: 'Taken', align: 'right', mono: true, sortable: true },
    { key: 'value', label: 'Earned', align: 'right', mono: true, sortable: true, render: (r) => formatCurrency(r.value) },
  ];

  return (
    <ReportsLayout>
      <PageHeader title="Modifiers" purpose="What people change, what the extras earn, and what keeps getting typed." testId="modifiers-report-header" />
      <FilterBar right={<span>{data?.orders ?? '—'} orders</span>}>
        <Segmented value={range} onChange={setRange} options={RANGES.map((r) => ({ ...r, testId: `modifiers-report-range-${r.value}` }))} />
      </FilterBar>
      <Stats className="mb-6" data-testid="modifiers-report-tiles">
        <Stat label="Lines changed" value={`${takeUp}%`} hint={t.sold ? `${t.changed} of ${t.sold} sold` : undefined} testId="modifiers-report-take-up" />
        <Stat label="From extras" value={formatCurrency(t.extras_revenue)} hint="priced options and library extras" testId="modifiers-report-extras" />
        <Stat label="Top earner" value={earners[0]?.name || '—'} hint={earners[0] ? `${formatCurrency(earners[0].value)} · ${earners[0].count} taken` : undefined} />
        <Stat label="Most typed" value={typed[0] ? `“${typed[0].text}”` : '—'} hint={typed[0] ? `${typed[0].count} times${typed[0].in_library ? '' : ' · not in the library'}` : undefined} delta={typed[0] && !typed[0].in_library ? { text: 'make it a modifier', tone: 'warn' } : undefined} testId="modifiers-report-typed" />
      </Stats>

      <h2 className="mb-2 font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">By item</h2>
      <DataTable columns={itemColumns} rows={items} rowKey={(r) => r.menu_item_id || r.name} loading={loading} defaultSort={{ key: 'take_up', dir: 'desc' }}
        empty={{ word: 'Modifiers', title: 'Nothing sold in this period' }} testId="modifiers-report-items" className="mb-8" />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <h2 className="mb-2 font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Typed in, not picked</h2>
          {typed.length === 0 && !loading ? <Empty word="Typed" title="Nothing typed in this period">Every change came from the groups or the library.</Empty>
            : <DataTable columns={typedColumns} rows={typed} rowKey="text" loading={loading} dense testId="modifiers-report-typed-table" />}
          <p className="mt-2 text-[12px] text-ink-faint">Something typed often should be a tap. Priced entries are on <Link to="/back-office/reports/typed-extras" className="text-verlo-hover underline">Typed extras</Link>.</p>
        </div>
        <div>
          <h2 className="mb-2 font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Top earners</h2>
          <DataTable columns={earnerColumns} rows={earners} rowKey="name" loading={loading} dense defaultSort={{ key: 'value', dir: 'desc' }}
            empty={{ word: 'Extras', title: 'No priced extras yet' }} testId="modifiers-report-earners" />
        </div>
      </div>
    </ReportsLayout>
  );
};

export default ModifiersReportPage;
